import { Button } from '@helvetia/components/Button'
import Input from '@helvetia/components/forms/Input'
import PhoneInput from '@helvetia/components/forms/PhoneInput'
import { Col, Row } from '@helvetia/components/layout'
import LinkText from '@helvetia/components/LinkText'
import { MatchMedia } from '@helvetia/components/MatchMedia'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import { Field, Form, Formik } from 'formik'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import AgentService from '../../core/services/agent.service'
import { CheckboxField } from '../_components/CheckboxField'
import { LifecycleView } from '../_components/ConnectComponent'
import { Modal } from '../_components/Modal'
import { AdobeEventManager } from '../_utils/adobeAnalytics'
import { goToPrivacyPolicyLink } from '../_utils/links'
import { ContactMePresenter } from './ContactMe.presenter'
import { validateFieldForm } from './ContactMe.validators'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

export interface ContactDataInfoValues {
  company: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  acceptedPrivacyPolicy: boolean
}

interface Props {
  presenter: ContactMePresenter
  onSubmit: (values: ContactDataInfoValues) => void
}

interface State {
  initialValues?: ContactDataInfoValues
  isLoading: boolean
  isModalOpen: boolean
  acceptedPrivacyPolicy: boolean
  hasReturnBackRoute: string
}

export class ContactMeRaw
  extends React.Component<Props & RouteComponentProps & WithNamespaces, State>
  implements View {
  private presenter: ContactMePresenter

  constructor(props) {
    super(props)

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.state = {
      initialValues: null,
      isLoading: false,
      isModalOpen: false,
      acceptedPrivacyPolicy: false,
      hasReturnBackRoute:
        this.props.history.location &&
          this.props.history.location.state &&
          this.props.history.location.state.from
          ? this.props.history.location.state.from
          : undefined,
    }
  }

  setInitialValues = initialValues => {
    this.setState({
      initialValues,
      acceptedPrivacyPolicy: initialValues.acceptedPrivacyPolicy,
    })
  }

  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  showModal = () => this.setState({ isModalOpen: true })

  hideModal = () => this.setState({ isModalOpen: false })

  handleClickContinuar = (values: ContactDataInfoValues) =>
    this.presenter.onContinue(values, this.state.hasReturnBackRoute)


  render() {
    const { t } = this.props
    const { isModalOpen } = this.state

    if (!this.state.initialValues) return <Spinner size="xl" />

    if (isModalOpen)
      return (
        <Modal
          buttonText={ t('buttons.close') }
          onClose={ this.presenter.onHideModalHandler }
          title={
            <>
              <MatchMedia onlyMobile>
                <Text size="sBold">{ t('modal.contact.title') }</Text>
              </MatchMedia>
              <MatchMedia tablet>
                <Text size="sBold">{ t('modal.contact.title') }</Text>
              </MatchMedia>
            </>
          }
        >
          <Text size="m" as="p" color="black">
            { t('modal.contact.body') }
          </Text>
        </Modal>
      )

    return (
      <>
        <Row>
          <Col tablet={ 12 } desktop={ 12 } mobile={ 12 }>
            <Row marginTop="xs" marginBottom="xs">
              <Text as="p" color="black" size="l">
                { t('contactMe.title') }
              </Text>
            </Row>
            <Row />

            <Formik
              initialValues={ this.state.initialValues }
              onSubmit={ this.handleClickContinuar }
              validate={ values => validateFieldForm(values) }
              render={ ({ setFieldValue, values }) => (
                <Form>
                  <Row marginTop="s" marginBottom="s">
                    <Col>
                      <Field
                        component={ Input }
                        name="company"
                        text={
                          AgentService.isAgentOrBroker()
                            ? t('contactData.company.agentQuestion')
                            : t('contactData.company.question')
                        }
                        label={ t('contactData.company.label') }
                        required
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col mobile={ 12 } tablet={ 6 } desktop={ 6 }>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={ Input }
                            name="firstName"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.firstName.agentQuestion')
                                : t('contactData.firstName.question')
                            }
                            label={ t('contactData.firstName.label') }
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col mobile={ 12 } tablet={ 6 } desktop={ 6 }>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={ Input }
                            name="lastName"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.lastName.agentQuestion')
                                : t('contactData.lastName.question')
                            }
                            label={ t('contactData.lastName.label') }
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col mobile={ 12 } tablet={ 6 } desktop={ 6 }>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={ Input }
                            name="email"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.email.agentQuestion')
                                : t('contactData.email.question')
                            }
                            label={ t('contactData.email.label') }
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col mobile={ 12 } tablet={ 6 } desktop={ 6 }>
                      <Row marginBottom="s">
                        <Col mobile={ 12 }>
                          <Field
                            component={ PhoneInput }
                            name="phone"
                            text={
                              AgentService.isAgentOrBroker()
                                ? `${t('contactData.phone.agentQuestion')}${t(
                                  'contactData.phone.optional',
                                )}`
                                : `${t('contactData.phone.question')}${t(
                                  'contactData.phone.optional',
                                )}`
                            }
                            label={ t('contactData.phone.label') }
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  { !AgentService.isAgentOrBroker() && (
                    <Row>
                      <Col>
                        <Field
                          component={ CheckboxField }
                          name="acceptedPrivacyPolicy"
                          value={ values.acceptedPrivacyPolicy }
                          defaultChecked={ values.acceptedPrivacyPolicy }
                          label={
                            <div>
                              <Text as="div" size="s">
                                { t('contactData.privacyPolicy.description') }
                                <LinkText
                                  onClick={ goToPrivacyPolicyLink }
                                  text={ t('contactData.privacyPolicy.link') }
                                  color="primary"
                                />
                              </Text>
                            </div>
                          }
                          onSelection={ () => {
                            setFieldValue(
                              'acceptedPrivacyPolicy',
                              !values.acceptedPrivacyPolicy,
                            )
                            this.setState({
                              acceptedPrivacyPolicy: !this.state
                                .acceptedPrivacyPolicy,
                            })
                          } }
                        />
                      </Col>
                    </Row>
                  ) }

                  <Row verticalGutter marginTop="xs" marginBottom="m">
                    <Col verticalGutter mobile={ 12 } tablet={ 3 } desktop={ 3 }>
                      <Button
                        primary
                        type="submit"
                        disabled={
                          !AgentService.isAgentOrBroker() &&
                          !Boolean(this.state.acceptedPrivacyPolicy)
                        }
                      >
                        { t('contactMe.send') }
                      </Button>
                    </Col>
                  </Row>
                </Form>
              ) }
            />
          </Col>
        </Row>
      </>
    )
  }
}

export const ContactMeView = withNamespaces()(withRouter(ContactMeRaw))
